import React, { useState, useMemo, useContext, useEffect, useRef } from 'react';
import { Layout } from './Layout';
import { MetricCard } from './MetricCard';
import { AppContext } from '../src/App';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import { Separator } from './ui/separator';
import { Progress } from './ui/progress';
import {
  Home,
  ArrowLeft,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Eye,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  Database,
  Bot,
  Timer,
  Activity,
  Zap,
  Settings,
  Users,
  Target,
  MessageSquare,
  User,
  Download,
  MoreHorizontal,
  RotateCcw,
  CircleUser,
  Undo,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  SearchXIcon,
  RefreshCw
} from 'lucide-react';
import { useAuth } from './AuthContext';
import { downloadAuditZip } from './ui/downloadHelpers';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { apiClient } from '@/Utils/apiClient';
import { ROUTES } from '@/routes/config';
// import { Dialog, DialogContent } from '@radix-ui/react-dialog';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle, Dialog, DialogContent } from './ui/dialog';
import { toast } from 'sonner';
import { PaginationCustom } from './ui/pagination-custom';
import { set } from 'date-fns';


// Generate mock audit goals data for a specific harvest run
const generateMockAuditGoalsData = (totalCharts: number, runId: string) => {
  const data = [];
  const clients = [
    { name: 'Christopher Lee', id: 'PT0342' },
    { name: 'Sarah Johnson', id: 'PT0158' },
    { name: 'Michael Chen', id: 'PT0279' },
    { name: 'Emma Davis', id: 'PT0421' },
    { name: 'David Wilson', id: 'PT0363' },
    { name: 'Jennifer Martinez', id: 'PT0195' },
    { name: 'Robert Brown', id: 'PT0287' },
    { name: 'Lisa Anderson', id: 'PT0412' }
  ];
  const clinicians = [
    { name: 'Dr. James Park', id: 'DR1002' },
    { name: 'Dr. Sarah Mitchell', id: 'DR1015' },
    { name: 'Dr. Robert Kim', id: 'DR1028' },
    { name: 'Dr. Lisa Chen', id: 'DR1041' },
    { name: 'Dr. Mark Johnson', id: 'DR1054' },
    { name: 'Dr. Emily Davis', id: 'DR1067' },
    { name: 'Dr. Michael Lee', id: 'DR1080' },
    { name: 'Dr. Amanda Wilson', id: 'DR1093' }
  ];
  const goalSummaries = [
    { text: 'Evaluate treatment effectiveness and client progress', category: 'Care Coordination' },
    { text: 'Monitor medication adherence and side effects', category: 'Medication Management' },
    { text: 'Assess pain management and comfort levels', category: 'Pain Management' },
    { text: 'Review discharge planning and follow-up care', category: 'Discharge Planning' },
    { text: 'Evaluate wound healing and infection prevention', category: 'Wound Care' },
    { text: 'Monitor vital signs and physiological stability', category: 'Monitoring' },
    { text: 'Assess nutritional status and dietary compliance', category: 'Nutrition' },
    { text: 'Review mental health status and coping mechanisms', category: 'Mental Health' },
    { text: 'Coordinate care between multiple specialists', category: 'Care Coordination' },
    { text: 'Monitor chronic disease progression and management', category: 'Chronic Care' }
  ];
  const aiSuggestions = ['Compliant', 'Deficient', 'Needs Review', 'Partially Met', 'Excellent', 'Non-Compliant'];
  const statuses = ['Pending', 'Approved', 'Needs Review', 'Completed'];
  const statusWeights = [0.4, 0.3, 0.2, 0.1]; // 40% pending, 30% approved, 20% needs review, 10% completed

  for (let i = 1; i <= totalCharts; i++) {
    const client = clients[Math.floor(Math.random() * clients.length)];
    const clinician = clinicians[Math.floor(Math.random() * clinicians.length)];
    const goalSummary = goalSummaries[Math.floor(Math.random() * goalSummaries.length)];
    const aiSuggestion = aiSuggestions[Math.floor(Math.random() * aiSuggestions.length)];

    // Determine status based on weights
    const statusRandom = Math.random();
    let status;
    if (statusRandom < (statusWeights[0] || 0)) status = 'Pending';
    else if (statusRandom < ((statusWeights[0] || 0) + (statusWeights[1] || 0))) status = 'Approved';
    else if (statusRandom < ((statusWeights[0] || 0) + (statusWeights[1] || 0) + (statusWeights[2] || 0))) status = 'Needs Review';
    else status = 'Completed';

    // Confidence based on AI suggestion
    const confidence = aiSuggestion === 'Excellent'
      ? Math.floor(Math.random() * 10) + 90 // 90-100%
      : aiSuggestion === 'Compliant'
        ? Math.floor(Math.random() * 15) + 80 // 80-95%
        : aiSuggestion === 'Partially Met'
          ? Math.floor(Math.random() * 20) + 65 // 65-85%
          : aiSuggestion === 'Needs Review'
            ? Math.floor(Math.random() * 15) + 60 // 60-75%
            : aiSuggestion === 'Deficient'
              ? Math.floor(Math.random() * 20) + 40 // 40-60%
              : Math.floor(Math.random() * 30) + 30; // 30-60% for Non-Compliant

    data.push({
      id: `${runId}-AG${String(i).padStart(4, '0')}`,
      runId,
      client,
      clinician,
      visitDate: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000), // Last 90 days
      goalSummary,
      aiSuggestion,
      confidence,
      status,
    });
  }

  return data.sort((a, b) => a.client?.name.localeCompare(b.client?.name || '') || 0);
};

// Mock prompt details
const mockPromptDetails = {
  name: 'Healthcare Audit Goals Evaluator',
  version: 'v2.3.1',
  model: 'Claude-3.5-Sonnet',
  description: 'Advanced AI prompt for evaluating healthcare audit goals against regulatory standards and best practices',
  lastUpdated: new Date('2024-12-10'),
  totalTokensUsed: 1247896,
  averageTokensPerChart: 1834,
  parameters: {
    temperature: 0.1,
    maxTokens: 4000,
    topP: 0.95,
  },
  categories: [
    'Clinical Documentation',
    'Medication Management',
    'Care Coordination',
    'Compliance Standards',
    'Quality Metrics'
  ]
};

export function HarvestRunView() {
  const { user, token } = useAuth();
  const { selectedHarvestRunData, currentPageHarvestRun, sideBarItems, setCurrentView, setSelectedHarvestRunData, setSelectedAuditData, setCurrentPageHarvestRuns, setCurrentIndexOfListedChart, currentIndexOfListedChart, setPreviousViewScreen, orgConfig } = useContext(AppContext);
  const [harvestRuns, setHarvestRuns] = useState<any>([])
  const location = useLocation();
  const passedRunData = location.state?.harvesRun;
  const runId = passedRunData?.run_id
  const navigate = useNavigate();
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<any>(null);
  const [dropdownVal, setDropdownVal] = useState([])
  const [selectedRerunCharts, setSelectedRerunCharts] = useState();
  const [selectedUndoCharts, setSelectedUndoCharts] = useState<any>(null);
  const auditorDecisions = ["AGREED", "DISAGREED", "REVIEW NEEDED", "ALL"
  ]
  const [loading, setLoading] = useState(false);
  const aiDecisions = ["Approved", "Send For Correction", "ALL"]
  const kanTimeStatus = ["Pending", "No Action", "Updated", "ALL"]
  const aiStatusOptions = ["Completed", "Failed", "All"]
  const chartReviewMenu = sideBarItems.find((item: any) => item.name === "Chart Review");
  const promptMenu = sideBarItems.find((item: any) => item.name === "Prompts");
  // Add this inside HarvestRunView component
  const [sortConfig, setSortConfig] = useState<{ sortBy: string | null; sortOrder: 'asc' | 'desc' | null }>({
    sortBy: null,
    sortOrder: null,
  });
  useEffect(() => {
    if (!selectedHarvestRunData && passedRunData) {
      setSelectedHarvestRunData(passedRunData);
      console.log('Setting selected harvest run data:selectedHarvestRunData', selectedHarvestRunData);
      // fetchData();
    }
  }, [])

  const hasPermission = (permission: string) => {
    return chartReviewMenu?.userPermissions?.includes(permission);
  }
  const hasPromptPermission = (permission: string) => {
    return promptMenu?.userPermissions?.includes(permission);
  }
  const isAdmin = hasPermission("chart:create")

  const handleSort = (field: string) => {
    setSortConfig((prev) => {
      // If clicking the same field
      if (prev.sortBy === field) {
        if (prev.sortOrder === 'asc') return { sortBy: field, sortOrder: 'desc' };
        if (prev.sortOrder === 'desc') return { sortBy: null, sortOrder: null }; // Reset
      }
      // If clicking a new field, start with asc
      return { sortBy: field, sortOrder: 'asc' };
    });
  };

  const renderSortableHeader = (label: string, fieldKey: string) => {
    const isSorted = sortConfig.sortBy === fieldKey;
    return (
      <div
        className="flex items-center gap-2 cursor-pointer select-none group"
        onClick={() => handleSort(fieldKey)}
      >
        {label}
        <span className="text-gray-400">
          {isSorted && sortConfig.sortOrder === 'asc' && <ArrowUp className="w-4 h-4 text-[#758195]" />}
          {isSorted && sortConfig.sortOrder === 'desc' && <ArrowDown className="w-4 h-4 text-[#758195]" />}
          {(!isSorted) && (
            <ArrowUpDown className="w-4 h-4 text-[#758195]" />
          )}
        </span>
      </div>
    );
  };
  const fetchPrompts = async () => {
    console.log('Fetching prompts...');
    try {
      const response = await apiClient('prompts', {
        method: 'GET',
        token
      })
      const data = await response.json();
      setDropdownVal(data?.data);
    }
    catch (error: any) {
      console.error('Error fetching categories:', error.message);
    }
  }
  // Fetch prompts when component mounts or when sideBarItems/promptMenu becomes available
  // This ensures prompts are fetched after refresh even if selectedHarvestRunData is null
  useEffect(() => {
    console.log('HarvestRunView - selectedHarvestRunData:', selectedHarvestRunData, 'promptMenu:', promptMenu, 'sideBarItems length:', sideBarItems?.length);
    // Fetch prompts if permission is available (will run when sideBarItems is ready after refresh)
    if (hasPromptPermission('prompts:view')) {
      fetchPrompts();
    }
  }, [selectedHarvestRunData, sideBarItems]);

  const fetchData = async () => {

    setLoading(true)
    try {
      let filterItems = {};
      if (currentIndexOfListedChart?.filters.aiDecision || currentIndexOfListedChart?.filters.auditorDecision ||
        currentIndexOfListedChart?.filters.KTStatus || currentIndexOfListedChart?.filters.aiStatus || currentIndexOfListedChart?.filters.searchTerm || sortConfig.sortBy || sortConfig.sortOrder) {
        filterItems = {
          aiDecision: currentIndexOfListedChart?.filters.aiDecision !== 'ALL' ? currentIndexOfListedChart?.filters.aiDecision : null,
          auditorDecision: currentIndexOfListedChart?.filters.auditorDecision === 'AGREED' ? 'Approved' : currentIndexOfListedChart?.filters.auditorDecision === 'DISAGREED' ? 'Rejected' : currentIndexOfListedChart?.filters.auditorDecision === 'REVIEW NEEDED' ? 'Pending' : currentIndexOfListedChart?.filters.auditorDecision === 'ALL' ? null : '',
          ktStatus: currentIndexOfListedChart?.filters.KTStatus !== 'ALL' ? currentIndexOfListedChart?.filters.KTStatus : null,
          aiStatus: currentIndexOfListedChart?.filters.aiStatus !== 'All' ? currentIndexOfListedChart?.filters.aiStatus : null,
          search: currentIndexOfListedChart?.filters.searchTerm,
          sortBy: sortConfig.sortBy,
          sortOrder: sortConfig.sortOrder
        }
      }

      const params = new URLSearchParams();
      for (const key in filterItems) {
        if (filterItems[key] != null) { // Only append if the value exists
          params.append(key, filterItems[key]);
        }
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}harvest/runs/${selectedHarvestRunData?.run_id || runId}?${params.toString()}`, {


        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          // Add auth token if needed
          'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      // const newData = await generateMockHarvestData(data)
      const a = data?.data?.harvestRun?.audits
      const b = data?.data?.harvestRun?.audits

      setHarvestRuns(data?.data?.harvestRun?.audits)
      return data;
    } catch (error: any) {
      console.error('Fetch error:', error.message);
      return null;
    }
    finally {
      setLoading(false)
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentIndexOfListedChart.filters.auditorDecision,
  currentIndexOfListedChart.filters.aiDecision,
  currentIndexOfListedChart.filters.KTStatus,
  currentIndexOfListedChart.filters.aiStatus,
  currentIndexOfListedChart.filters.searchTerm,
    runId,
    sortConfig])
  // clear filter
  const handleClearFilter = () => {
    setCurrentIndexOfListedChart({
      index: 0,
      filterItems: [],
      filters: {
        aiDecision: "",
        auditorDecision: "",
        KTStatus: "",
        aiStatus: "",
        searchTerm: ""
      }
    })
    setSortConfig({ sortBy: null, sortOrder: null });
  }
  // Show loading/error state if no data is available
  if (!harvestRuns) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white">
          <div className="p-8">
            {/* Breadcrumb Navigation */}
            <div className="mb-6">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="#" className="flex items-center gap-2">
                      <Home className="w-3 h-3" />
                      Dashboard
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink href="#" onClick={() => {
                      setCurrentView('chat-harvest')
                      navigate(ROUTES.chartReviewList)
                    }}>
                      Chat Harvest
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Audit Goals Detail</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            {/* Error State */}
            <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
              <CardContent className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-gray-900 font-medium mb-2">No Harvest Run Selected</h3>
                  <p className="text-gray-600 mb-4">
                    No harvest run data was found. Please go back to the Chat Harvest dashboard and select a completed run to view details.
                  </p>
                  <Button onClick={() => { setCurrentView('chat-harvest'); handleClearFilter(); setCurrentPageHarvestRuns(1); navigate(ROUTES.chartReviewList); }} className="bg-blue-600 hover:bg-blue-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Chat Retrival
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  const runData = selectedHarvestRunData;

  // Generate audit goals data for this specific run
  const allGoalsData = useMemo(() =>
    generateMockAuditGoalsData(runData?.totalCharts, runData?.id),
    [runData?.totalCharts, runData?.id]
  );


  // Filter and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(currentPageHarvestRun);
  const itemsPerPage = 15;
  const [runFlag, setRunFlag] = useState(1);

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    return allGoalsData.filter(goal => {
      const matchesSearch = searchTerm === '' ||
        goal.client?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        goal.client?.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        goal.clinician?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        goal.clinician?.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        goal.goalSummary?.text.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || goal.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [allGoalsData, searchTerm, statusFilter]);


  // Pagination
  const totalPages = Math.ceil(harvestRuns.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = harvestRuns.slice(startIndex, startIndex + itemsPerPage);
  console.log('paginatedData', paginatedData)
  //   useEffect(() => {
  //     const noFiltersSelected =
  //     !currentIndexOfListedChart.filters.auditorDecision &&
  //     !currentIndexOfListedChart.filters.aiDecision &&
  //     !currentIndexOfListedChart.filters.KTStatus;

  //   setCurrentIndexOfListedChart(prev => ({
  //     ...prev,
  //     index: 0, // reset safely when filters change
  //     filterItems: noFiltersSelected ? [] : harvestRuns, 
  //   }));
  //   // setCurrentIndexOfListedChart(prev => ({
  //   //   ...prev,
  //   //   index: 0, // reset index when filter changes
  //   //   filterItems: paginatedData, 
  //   // }));
  // }, [paginatedData]);
  useEffect(() => {
    // Only update if filters have actually changed
    const filterValues = [
      currentIndexOfListedChart.filters.auditorDecision,
      currentIndexOfListedChart.filters.aiDecision,
      currentIndexOfListedChart.filters.KTStatus,
      currentIndexOfListedChart.filters.aiStatus,
      currentIndexOfListedChart.filters.searchTerm,
      sortConfig.sortBy,
      sortConfig.sortOrder
    ];
    const hasFilters = filterValues.some(val => val);
    setCurrentIndexOfListedChart(prev => ({
      ...prev,
      index: 0, // reset index when filters change
      filterItems: hasFilters ? harvestRuns : [],
    }));
  }, [
    currentIndexOfListedChart.filters.auditorDecision,
    currentIndexOfListedChart.filters.aiDecision,
    currentIndexOfListedChart.filters.KTStatus,
    currentIndexOfListedChart.filters.aiStatus,
    harvestRuns,
    currentIndexOfListedChart.filters.searchTerm,
    sortConfig
  ]);
  // Reset pagination when filters change
  React.useEffect(() => {
    if (searchTerm === "" && statusFilter === 'all') {
      const totalPages = Math.ceil(harvestRuns.length / itemsPerPage);
      if (totalPages >= currentPageHarvestRun) {
        setCurrentPage(currentPageHarvestRun);
      } else {
        setCurrentPage(1);
      }
    } else {
      setCurrentPage(1);
    }
  }, [searchTerm, statusFilter, totalPages]);

  const allGoals = selectedHarvestRunData?.total_completed_goals + selectedHarvestRunData?.total_approved_goals + selectedHarvestRunData?.total_pending_goals;

  // Calculate KPIs
  const kpiMetrics = useMemo(() => {
    const pendingGoals = selectedHarvestRunData?.total_pending_goals;
    const approvedGoals = selectedHarvestRunData?.total_approved_goals;
    const reviewGoals = selectedHarvestRunData?.charts_needs_review_count;
    const completedGoals = selectedHarvestRunData?.total_completed_goals;

    const avgConfidence = selectedHarvestRunData?.overall_average_confidence_score;
    const pendingRate = (pendingGoals / allGoals) * 100;
    const approvedRate = (approvedGoals / allGoals) * 100;
    const reviewRate = (reviewGoals / allGoals) * 100;
    const completedRate = (completedGoals / allGoals) * 100;

    // Mock trends (in a real app, these would be calculated from historical data)
    const generateTrend = (isPositive: boolean) => ({
      percentage: Math.random() * 15 + 2,
      isPositive,
      hasData: true,
    });

    return {
      totalGoals: allGoals,
      pendingGoals,
      approvedGoals,
      reviewGoals,
      completedGoals,
      pendingRate: pendingRate.toFixed(1),
      approvedRate: approvedRate.toFixed(1),
      reviewRate: reviewRate.toFixed(1),
      completedRate: completedRate.toFixed(1),
      avgConfidence: Math.floor(avgConfidence),
      trends: {
        pending: generateTrend(false),
        approved: generateTrend(true),
        review: generateTrend(false),
        completed: generateTrend(true),
        confidence: generateTrend(true),
      }
    };
  }, [allGoalsData]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'Pending': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
      'Approved': { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      'Needs Review': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Eye },
      'Completed': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: CheckCircle },
      'NON_COMPLIANT': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Eye }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig['Pending'];
    const IconComponent = config.icon;

    return (
      <Badge variant="outline" className={`${config.color} gap-1 text-xs`}>
        <IconComponent className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const getAiSuggestionBadge = (suggestion: string) => {
    const suggestionConfig = {
      'Excellent': { color: 'bg-green-100 text-green-800 border-green-200' },
      'Compliant': { color: 'bg-green-100 text-green-800 border-green-200' },
      'Partially Met': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      'Needs Review': { color: 'bg-blue-100 text-blue-800 border-blue-200' },
      'Deficient': { color: 'bg-red-100 text-red-800 border-red-200' },
      'Non-Compliant': { color: 'bg-red-100 text-red-800 border-red-200' }
    };

    const config = suggestionConfig[suggestion as keyof typeof suggestionConfig] || suggestionConfig['Needs Review'];

    return (
      <Badge variant="outline" className={`${config.color} text-xs`}>
        {suggestion}
      </Badge>
    );
  };

  const getReviewBadge = (suggestion: string) => {
    const newSug = suggestion?.toLocaleLowerCase()
    const suggestionConfig = {
      'approved': { color: 'bg-green-100 text-green-800 border-green-200' },
      'agreed': { color: 'bg-green-100 text-green-800 border-green-200' },

      // 'rejected': { color: 'bg-green-100 text-green-800 border-green-200' },
      'pending': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      'Needs Review': { color: 'bg-blue-100 text-blue-800 border-blue-200' },
      'disagreed': { color: 'bg-red-100 text-red-800 border-red-200' },

      'rejected': { color: 'bg-red-100 text-red-800 border-red-200' },
      'Non-Compliant': { color: 'bg-red-100 text-red-800 border-red-200' },
      'review needed': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200' }
    };

    const config = suggestionConfig[newSug as keyof typeof suggestionConfig] || suggestionConfig['Needs Review'];

    return (
      <Badge variant="outline" className={`${config.color} text-sm font-medium`}>
        {suggestion}
      </Badge>
    );
  };

  const handleDownload = async (auditId: string, chartId: string) => {
    downloadAuditZip({ id: auditId, token, chartId });
  };
  const handleReTrychart = async (chartData: any) => {
    // reRunAudit({ id: auditId, token, chartId });
    setSelectedRerunCharts(chartData);

    // const getCategory=chartData.section.split(': ')[0]
    const sectionRaw = chartData?.section || "";
    const match = sectionRaw.match(/^(Section \d+)/);
    let getCategory = match ? match[1] : chartData.section;

    if (getCategory === "Section 28") {
      getCategory = "CSC";
    } else if (getCategory === "Section 17") {
      getCategory = "DLS";
    }
    

    // if(dropdownVal.length === 0)  fetchPrompts();
    const filtered = dropdownVal.filter((item: any) => {
      // console.log('item',item.category,getCategory==item.category.trim(),typeof item.category,typeof getCategory)
      if (getCategory == item.category && (item.status === "ACTIVE" || item.status === "TEST")) {
        return item;
      }
    });

    setDropdownVal(filtered);
    setShowRunDialog(true);
  };
  const handleBackToHarvest = () => {
    console.log('Navigating back to chat-harvest');
    setSelectedHarvestRunData(undefined);
    setCurrentView('chat-harvest');
    handleClearFilter()
    setCurrentPageHarvestRuns(1)
    navigate(ROUTES.chartReviewList)
  };
  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    handleClearFilter()
  }

  const handleGoalRowClick = (goal: any, index: number) => {
    console.log('Goal row clicked:', goal);
    // setCurrentIndexOfListedChart(prev=>(...prev,index:index));
    setCurrentIndexOfListedChart((prev: { index: number }) => ({
      ...prev,
      index: index  // or whatever new value you want
    }))
    let data = { ...goal, run_id: selectedHarvestRunData?.run_id }
    setSelectedAuditData(data);
    setPreviousViewScreen('harvest-run-detail');
    // if(goal.prompt_name === 'SN Charts'){
    //   setCurrentView('sn-chart-detail');
    // }else{
    setCurrentView('audit-goals-detail');
    // }
    const auditIdentifier = selectedHarvestRunData?.run_id;
    const chartIndex = { ...currentIndexOfListedChart, index: index }
    if (auditIdentifier) {
      navigate(ROUTES.auditDetail, {
        state: {
          auditData: data,
          previousScreen: 'harvest-run-detail',
          currentchartIndex: chartIndex,
          auditId: auditIdentifier
        }
      });
    }

  };
  const UndoChartsDecision = async () => {
    console.log('selectedUndoCharts', selectedUndoCharts)
    try {
      const response = await apiClient(`audits/${selectedUndoCharts?.id}`, {
        method: 'PUT',
        token,
        body: {
          outcome: "Pending",
          supervisorComment: ""
        }
      }


      )
      const data = await response.json();
      if (data?.success) {
        toast.success('Undo Decision successfully');
      }

    }
    catch (error: any) {
      toast.error('Undo Decision failed');
      console.error('Error fetching categories:', error.message);
    }
    finally {
      fetchData();
      setSelectedUndoCharts(null);
    }

  }

  const rerunIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rerunToastIdRef = useRef<string | number | null>(null);
  const rerunClientInfoRef = useRef<{ clientName: string; clientId: string } | null>(null);
  const [isRerunPolling, setIsRerunPolling] = useState(false);

  useEffect(() => {
    return () => {
      if (rerunIntervalRef.current) {
        clearInterval(rerunIntervalRef.current);
        rerunIntervalRef.current = null;
      }
      if (rerunToastIdRef.current) {
        toast.dismiss(rerunToastIdRef.current);
        rerunToastIdRef.current = null;
      }
    };
  }, []);

  const ReRunChart = async () => {
    // const versionId = dropdownVal.filter(item => item?.prompt_id === selectedPrompt)[0]?.version_id;
    //     setHarvestRuns(prev =>
    //   prev.map(item =>
    //     item.chart_id === selectedRerunCharts?.chart_id ? { ...item, running_status: "Processing" } : item
    //   )
    // );

    try {
      if (isRerunPolling) return;
      setIsRerunPolling(true);
      
      // Store client information for use in completion messages
      const clientName = selectedRerunCharts?.client_name || 'Unknown Client';
      const clientId = selectedRerunCharts?.client_id || '';
      rerunClientInfoRef.current = { clientName, clientId };
      
      // Create descriptive toast message with client information
      const toastMessage = clientId 
        ? `Re-running chart for ${clientName} (${clientId}). Processing...`
        : `Re-running chart for ${clientName}. Processing...`;
      
        
      rerunToastIdRef.current = toast.loading(toastMessage);

      const response = await apiClient(`harvest/runs?runFlag=${runFlag}&harvestRunId=${selectedHarvestRunData?.id}&chartIds=${selectedRerunCharts?.chart_id}&promptVersionId=${selectedPrompt}&rerunMode=any`,
        {
          method: 'POST',
          token
        }
      );
      const result = await response.json();


      const pollInterval = 40000;

      const intervalId = setInterval(async () => {
        try {
          const statusResponse = await apiClient(`harvest/runs/status/${selectedHarvestRunData?.id}`, {
            method: 'GET',
            token

          });
          const statusData = await statusResponse.json();
          // toast.info('Re run completed');
          if (statusData?.status === 'FAILED') {
            clearInterval(intervalId);
            rerunIntervalRef.current = null;
            setIsRerunPolling(false);
            if (rerunToastIdRef.current) {
              toast.dismiss(rerunToastIdRef.current);
              rerunToastIdRef.current = null;
            }
            await fetchData();
            const clientInfo = rerunClientInfoRef.current;
            const errorMessage = clientInfo?.clientId
              ? `Re-run failed for ${clientInfo.clientName} (${clientInfo.clientId}). Please try again.`
              : clientInfo?.clientName
              ? `Re-run failed for ${clientInfo.clientName}. Please try again.`
              : 'Re-run failed. Please try again.';
            toast.error(errorMessage);
            rerunClientInfoRef.current = null;
          }

          if (statusData?.status === 'COMPLETED') {
            clearInterval(intervalId);
            rerunIntervalRef.current = null;
            setIsRerunPolling(false);
            if (rerunToastIdRef.current) {
              toast.dismiss(rerunToastIdRef.current);
              rerunToastIdRef.current = null;
            }
            const clientInfo = rerunClientInfoRef.current;
            const successMessage = clientInfo?.clientId
              ? `Re-run completed successfully for ${clientInfo.clientName} (${clientInfo.clientId})`
              : clientInfo?.clientName
              ? `Re-run completed successfully for ${clientInfo.clientName}`
              : 'Re-run Successfully completed';
            toast.success(successMessage);
            await fetchData(); // call your data fetcher
            rerunClientInfoRef.current = null;
          }
        } catch (err) {
          console.error('Polling error:', err);
          // toast.error(err?.message);
          clearInterval(intervalId); // Optional: stop polling on error
          rerunIntervalRef.current = null;
          setIsRerunPolling(false);
          if (rerunToastIdRef.current) {
            toast.dismiss(rerunToastIdRef.current);
            rerunToastIdRef.current = null;
          }
          const clientInfo = rerunClientInfoRef.current;
          const errorMessage = clientInfo?.clientId
            ? `Re-run status check failed for ${clientInfo.clientName} (${clientInfo.clientId}). Please try again.`
            : clientInfo?.clientName
            ? `Re-run status check failed for ${clientInfo.clientName}. Please try again.`
            : 'Re-run status check failed. Please try again.';
          toast.error(errorMessage);
          rerunClientInfoRef.current = null;
        }
      }, pollInterval);
      rerunIntervalRef.current = intervalId;

    }
    catch (error: any) {
      console.error('Re-run error:', error?.message);
      setIsRerunPolling(false);
      if (rerunToastIdRef.current) {
        toast.dismiss(rerunToastIdRef.current);
        rerunToastIdRef.current = null;
      }
      const clientInfo = rerunClientInfoRef.current;
      const errorMessage = clientInfo?.clientId
        ? `Re-run failed to start for ${clientInfo.clientName} (${clientInfo.clientId}). Please try again.`
        : clientInfo?.clientName
        ? `Re-run failed to start for ${clientInfo.clientName}. Please try again.`
        : 'Re-run failed to start. Please try again.';
      toast.error(errorMessage);
      rerunClientInfoRef.current = null;
    }
    finally {
      fetchData();
      setSelectedRerunCharts(null);
      setSelectedPrompt(null);
    }
  }
  const toCamelCase = (str: string) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .replace(/(^\w|\s\w)/g, (match) => match.toUpperCase());
  };
  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white">
        <div className="p-8">
          {/* Breadcrumb Navigation */}
          <div className="mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                {/* <BreadcrumbItem>
                  <BreadcrumbLink href="#" onClick={handleBackToDashboard} className="flex items-center gap-2">
                    <Home className="w-3 h-3" />
                    Dashboard
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator /> */}
                <BreadcrumbItem>
                  <BreadcrumbLink href="#" onClick={handleBackToHarvest}>
                    Chart Review
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Chart Review Detail</BreadcrumbPage>
                  {/* <BreadcrumbPage>Chart Details</BreadcrumbPage> */}
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Header with Back Button */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={handleBackToHarvest}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Chart Review
              </Button>
              <div>
                <h1 className="text-gray-900 mb-1">
                  {/* Retrieval Run Detail - {runData?.run_id}  -  */}

                  {/* <span className='font-medium'>{ isAdmin?<span className='font-semibold'>`${runData?.display_harvest_id}`</span>+" - "+runData?.harvest_file_name:runData?.display_harvest_id}</span>, {new Date(runData?.completed_at)?.toLocaleDateString('en-US')}, {new Date(runData?.completed_at)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', })} */}
                </h1><span className="font-medium">
                  {/* {isAdmin ? (
                    <>
                      <span className="font-medium">{runData?.display_harvest_id}</span>
                      {" - "}
                      {runData?.harvest_file_name}
                    </>
                  ) : (
                    <span className="font-medium">{runData?.display_harvest_id}</span>
                  )} */}
                  <span className="font-medium">{runData?.display_harvest_id}</span>
                </span>
                ,{" "}
                {new Date(runData?.started_at).toLocaleDateString("en-US")}
                ,{" "}
                {new Date(runData?.started_at).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}

                {/* <h1 className="text-gray-900 mb-1">
                  Chart Details
                </h1> */}
                <p className="text-gray-600">
                  {/* Executed on - {new Date(runData?.completed_at)?.toLocaleDateString('en-US')} */}
                  {/* {runData.executedAt.toLocaleDateString()} at {runData.executedAt.toLocaleTimeString()} */}
                </p>
              </div>
            </div>
            {/* <Badge variant="outline" className={runData.status === 'Completed' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50'}>
              {runData.status}
            </Badge> */}
          </div>

          {/* Prompt Details Section */}
          {/* <Card className="mb-8 bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                AI Prompt Configuration
              </CardTitle>
              <CardDescription>
                Details about the AI model and prompt used for this audit goals evaluation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Bot className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Prompt Details</span>
                  </div>
                  <p className="font-medium text-gray-900">{mockPromptDetails.name}</p>
                  <p className="text-sm text-gray-600">Version {mockPromptDetails.version}</p>
                  <p className="text-xs text-gray-500 mt-1">{mockPromptDetails.description}</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Settings className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Model & Config</span>
                  </div>
                  <p className="font-medium text-gray-900">{mockPromptDetails.model}</p>
                  <p className="text-sm text-gray-600">Temperature: {mockPromptDetails.parameters.temperature}</p>
                  <p className="text-sm text-gray-600">Max Tokens: {mockPromptDetails.parameters.maxTokens.toLocaleString()}</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Avg Confidence</span>
                  </div>
                  <p className="font-medium text-gray-900">{kpiMetrics.avgConfidence}%</p>
                  <p className="text-sm text-gray-600">Average goal confidence score</p>
                  <p className="text-xs text-gray-500 mt-1">AI assessment confidence</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Categories</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {mockPromptDetails.categories.slice(0, 3).map((category, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {category}
                      </Badge>
                    ))}
                    {mockPromptDetails.categories.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{mockPromptDetails.categories.length - 3}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card> */}

          {/* KPI Cards */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <MetricCard
              title="Pending Goals"
              value={kpiMetrics?.pendingGoals}
              icon={Clock}
              trend={kpiMetrics?.trends?.pending}
              description={`${kpiMetrics?.pendingRate}% pending`}
            />
            <MetricCard
              title="Approved Goals"
              value={kpiMetrics?.approvedGoals}
              icon={CheckCircle}
              trend={kpiMetrics?.trends?.approved}
              description={`${kpiMetrics?.approvedRate}% approved`}
            />
            <MetricCard
              title="Needs Review"
              value={kpiMetrics?.reviewGoals}
              icon={Eye}
              trend={kpiMetrics?.trends.review}
              description={`${kpiMetrics?.reviewRate}% review rate`}
            />
            <MetricCard
              title="Completed Goals"
              value={kpiMetrics?.completedGoals}
              icon={CheckCircle}
              trend={kpiMetrics?.trends?.completed}
              description={`${kpiMetrics?.completedRate}% completed`}
            />
            <MetricCard
              title="Avg Confidence"
              value={`${kpiMetrics?.avgConfidence}%`}
              icon={Target}
              trend={kpiMetrics?.trends?.confidence}
              description="AI assessment confidence"
            />
          </div> */}

          {/* Filters and Search */}
          {/* <Card className="mb-6 bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Filters</span>
                </div>

                <div className="flex flex-1 flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by client, clinician, or goal summary..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Approved">Approved</SelectItem>
                      <SelectItem value="Needs Review">Needs Review</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(searchTerm || statusFilter !== 'all') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>

              {filteredData.length !== allGoalsData.length && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-600">
                    Showing {filteredData?.length} of {harvestRuns?.length} audit goals
                  </p>
                </div>
              )}
            </CardContent>
          </Card> */}

          {/* Audit Goals Data Table */}
          <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
            <CardHeader>
              {/* <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                Audit Goals Detail
              </CardTitle> */}
              <div className='flex items-center gap-2 justify-between'>
                {/* <CardDescription className='text-[14px]'>
                Client level e-charts analysis and decisioning by AI.
              </CardDescription> */}
                <div className='flex-1  items-center gap-2'>
                  <div className="flex-1 min-w-[400px] w-full">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by Client Name..."
                        value={currentIndexOfListedChart.filters.searchTerm}
                        onChange={(e) => setCurrentIndexOfListedChart(prev => ({
                          ...prev,
                          filters: {
                            ...prev.filters,
                            searchTerm: e.target.value
                          }
                        }))}
                        className="pl-10 w-full"
                      />
                    </div>
                  </div>


                </div>
                <div className='flex items-center gap-2'>
                  <div>
                    <Select value={currentIndexOfListedChart?.filters.aiStatus} onValueChange={(value) => {
                      setCurrentIndexOfListedChart(prev => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          aiStatus: value
                        }
                      }));
                    }}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="AI Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {aiStatusOptions.map((status) => (
                          <SelectItem key={status} value={status}>
                            <span className='uppercase font-medium text-gray-600'>{status}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Select value={currentIndexOfListedChart.filters.aiDecision} onValueChange={(value) => {
                      setCurrentIndexOfListedChart(prev => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          aiDecision: value
                        }
                      }));
                    }}>
                      <SelectTrigger className="w-42">
                        <SelectValue placeholder="AI Decisions" />
                      </SelectTrigger>
                      <SelectContent>
                        {aiDecisions.map((category) => (
                          <SelectItem key={category} value={category}>
                            <span className=' font-inter uppercase font-medium text-gray-600'>{category}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select></div>
                  <div>
                    <Select value={currentIndexOfListedChart.filters.auditorDecision} onValueChange={(value) => {
                      setCurrentIndexOfListedChart(prev => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          auditorDecision: value
                        }
                      }));
                    }}>
                      <SelectTrigger className="w-42">
                        <SelectValue placeholder="Auditor Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {auditorDecisions.map((category) => (
                          <SelectItem key={category} value={category}>
                            <span className='uppercase font-medium text-gray-600'>{category}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Select value={currentIndexOfListedChart?.filters.KTStatus} onValueChange={(value) => {
                      setCurrentIndexOfListedChart(prev => ({
                        ...prev,
                        filters: {
                          ...prev.filters,
                          KTStatus: value
                        }
                      }));
                    }}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder={`${orgConfig?.name} Status`} />
                      </SelectTrigger>
                      <SelectContent>
                        {kanTimeStatus.map((category) => (
                          <SelectItem key={category} value={category}>
                            <span className='uppercase font-medium text-gray-600'>{category}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Button
                      variant="outline"
                      onClick={handleClearFilter}
                      className="font-inter font-semibold text-[14px] leading-[20px] tracking-normal text-[#2C3E5D]"
                    >
                      <RefreshCw className="w-4 h-4 mr-2 text-[#2C3E5D]" />
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto mx-6">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">{renderSortableHeader("Client id", "client_id")}</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">{renderSortableHeader("CLIENT", "client_name")}</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Visit Date</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]  ">{renderSortableHeader("Clinician", "clinician_name")}</TableHead>
                      {isAdmin && <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Prompts</TableHead>}
                      {/* {isAdmin && <TableHead className="font-medium text-gray-900 uppercase">AI Suggestion</TableHead>} */}
                      {/* {isAdmin && <TableHead className="font-medium text-gray-900 uppercase text-center">Confidence</TableHead>} */}
                      {/* {isAdmin && <TableHead className="font-medium text-gray-900 uppercase text-center">Client Id</TableHead>} */}
                      {isAdmin && <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]"> AI Status</TableHead>}
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]  text-wrap text-center">AI Decision</TableHead>
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]  text-wrap">Auditor Review Status</TableHead>
                      {isAdmin && <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">Auditor Comments</TableHead>}
                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195]">{`${orgConfig?.name} Status`}</TableHead>


                      <TableHead className="font-inter font-semibold text-[14px] leading-[100%] tracking-[-0.5%] uppercase text-[#758195] text-center ">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* {paginatedData?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12">
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                              <Search className="w-6 h-6 text-gray-400" />
                            </div>
                            <p className="text-gray-500">No Data found</p>
                           
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : */}
                    {/* {paginatedData?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center py-12">
                          No Data Found
                        </TableCell>
                      </TableRow>
                    )} */}
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center p-6">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : paginatedData?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={11}
                          className="p-12 text-center text-gray-600"
                        >
                          No Data Found
                        </TableCell>
                      </TableRow>
                    ) : paginatedData?.map((goal: any, index) => {
                      const outcome = goal?.outcome?.toLowerCase();

                      let reviewLabel;
                      if (outcome === "approved") reviewLabel = "AGREED";
                      else if (outcome === "rejected") reviewLabel = "DISAGREED";
                      else reviewLabel = "REVIEW NEEDED";
                      const originalIndex = startIndex + index;
                      return (
                        <TableRow
                          key={goal?.id}
                          className="hover:bg-blue-50/50 transition-colors cursor-pointer"
                          onClick={() => {
                            if (hasPermission("chart:view")) { handleGoalRowClick(goal, originalIndex); }
                            else toast.error("You need 'audits:view' permission to view chart details.");
                          }}
                          title="Click to view goal details"
                        >
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600">{goal?.client_id}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col text-wrap w-32">
                              <span className="font-medium text-gray-600">{goal?.client_name}</span>
                              {/* <div className="flex items-center gap-2 mt-1">
                              <User className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-600">{goal?.chart_id}</span>
                            </div> */}
                              <span className="text-xs text-gray-500">
                                {/* {goal?.visitDate?.toLocaleDateString()} */}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600">{goal?.visit_date}
                              </span>
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600">{goal?.clinician_name}</span>
                              {/* <span className="text-xs text-gray-600">{goal?.chart_id}</span> */}
                            </div>
                          </TableCell>

                          {isAdmin && <TableCell className="max-w-[120px] align-top">
                            <div className="flex flex-col w-full">
                              <span className="font-medium text-gray-500 break-words whitespace-normal w-full">
                                {goal?.prompt_name}
                              </span>

                              <span className="text-xs text-gray-600">
                                Version: {goal?.prompt_version}
                              </span>
                            </div>
                          </TableCell>}
                          {isAdmin && <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600 capitalize">{toCamelCase(goal?.running_status) || ""}
                              </span>
                            </div>
                          </TableCell>}

                          {/* {isAdmin && <TableCell>
                            {getAiSuggestionBadge(goal?.ai_suggestion || '')}
                          </TableCell>} */}

                          {/* {isAdmin && <TableCell className='flex items-center justify-center'>
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">{goal?.confidence_score}%</span>
                                </div>
                                <Progress
                                  value={goal?.confidence_score}
                                  className={`h-2 w-16 ${goal.confidence_score >= 80 ? '[&>div]:bg-green-500' :
                                    goal.confidence_score >= 60 ? '[&>div]:bg-yellow-500' : '[&>div]:bg-red-500'
                                    }`}
                                />
                              </div>
                            </TableCell>} */}
                          {/* {isAdmin && <TableCell className='flex items-center justify-center'>
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">{goal?.client_id}</span>
                                </div>
                                <Progress
                                  value={goal?.confidence_score}
                                  className={`h-2 w-16 ${goal.confidence_score >= 80 ? '[&>div]:bg-green-500' :
                                    goal.confidence_score >= 60 ? '[&>div]:bg-yellow-500' : '[&>div]:bg-red-500'
                                    }`}
                                />
                              </div>
                            </TableCell>} */}


                          <TableCell>
                            {/* {getStatusBadge(goal?.ai_suggestion || "Null")} */}
                            {/* {goal?.overall_audit_decision}*/}
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600 text-center">{goal?.overall_audit_decision}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className='flex flex-col gap-2 items-center '>
                              {getReviewBadge(reviewLabel)}
                              {/* {goal?.outcome!=="Approved" ||goal?.outcome!=="Rejected" && <div>
                                  <Button className='w-auto h-6' onClick={(e)=>{e.stopPropagation(),setSelectedUndoCharts(goal)}}>undo</Button>
                                </div>
                      } */}
                            </div>
                          </TableCell>

                          {isAdmin && <TableCell>
                            <div className='flex flex-col gap-2'>

                              {goal?.supervisor_comment ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        className="truncate cursor-help max-w-xs text-ellipsis overflow-hidden"
                                        onMouseEnter={(e) => e.stopPropagation()}
                                      >
                                        {goal.supervisor_comment}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="top"
                                      className="bg-black text-white p-3 rounded-md text-sm max-w-sm max-h-60 overflow-y-auto whitespace-pre-line shadow-lg border border-gray-300"
                                    >
                                      {goal.supervisor_comment}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>

                                // <TooltipProvider>
                                //   <Tooltip>
                                //     <TooltipTrigger asChild>
                                //       <span
                                //         className="truncate cursor-help max-w-xs text-ellipsis overflow-hidden"
                                //         onMouseEnter={(e) => e.stopPropagation()}
                                //       >
                                //         {goal.supervisor_comment}
                                //       </span>
                                //     </TooltipTrigger>
                                //     <TooltipContent side="top" className="bg-black text-white p-2 rounded text-sm max-w-xs">
                                //       {goal.supervisor_comment}
                                //     </TooltipContent>
                                //   </Tooltip>
                                // </TooltipProvider>
                              ) : <div className='text-center'>-</div>}
                            </div>
                          </TableCell>}


                          {/* <TableCell>
                            <div className="flex items-center justify-center gap-3">

                              <Eye className="w-4 h-4 text-blue-500" />
                              {isAdmin && <div
                                className="flex items-center justify-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(goal.id, goal?.chart_id);
                                }}
                              >
                                <Download className="w-4 h-4 text-blue-500" />
                              </div>}

                            </div>

                          </TableCell> */}
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-600">{goal?.kt_update || ""}
                              </span>
                            </div>
                          </TableCell>

                          <TableCell className='flex items-center justify-center'>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => { }}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View
                                </DropdownMenuItem>
                                {hasPermission("chart:create") &&
                                  <>
                                    <DropdownMenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      handleReTrychart(goal);
                                      // setSelectedRerunCharts(goal);
                                    }}>
                                      <></>
                                      <RotateCcw className="w-4 h-4 mr-2 color-blue" />
                                      Re-Run Chart
                                    </DropdownMenuItem>
                                  
                                    {hasPermission("audits:review") && (!goal?.kt_update || goal?.kt_update === "Pending") && (goal?.outcome === "Approved" || goal?.outcome === "Rejected") && <DropdownMenuItem onClick={(e) => {
                                      e.stopPropagation();

                                      setSelectedUndoCharts(goal);
                                    }}>
                                      <></>
                                      <Undo className="w-4 h-4 mr-2" />
                                      Undo Decision
                                    </DropdownMenuItem>}
                                    <DropdownMenuItem onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(goal.id, goal?.chart_id);
                                    }}>
                                      <></>
                                      <Download className="w-4 h-4 mr-2" />
                                      Download
                                    </DropdownMenuItem>
                                    {/* <DropdownMenuSeparator /> */}

                                  </>}

                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>

                        </TableRow>
                      )
                    })}
                    {/* } */}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {/* {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, harvestRuns?.length)} of {harvestRuns?.length} eCharts
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setCurrentPage(Math.max(1, currentPage - 1)); setCurrentPageHarvestRuns(Math.max(1, currentPage - 1)) }}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => { setCurrentPage(page); setCurrentPageHarvestRuns(page) }}
                            className="w-8 h-8 p-0"
                          >
                            {page}
                          </Button>
                        );
                      })}
                      {totalPages > 5 && (
                        <>
                          <span className="text-gray-500">...</span>
                          <Button
                            variant={currentPage === totalPages ? "default" : "outline"}
                            size="sm"
                            onClick={() => { setCurrentPage(totalPages); setCurrentPageHarvestRuns(totalPages) }}
                            className="w-8 h-8 p-0"
                          >
                            {totalPages}
                          </Button>
                        </>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setCurrentPage(Math.min(totalPages, currentPage + 1)); setCurrentPageHarvestRuns(Math.min(totalPages, currentPage + 1)) }}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )} */}
              {totalPages > 1 && (<div className='px-4'><PaginationCustom
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={harvestRuns.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  setCurrentPageHarvestRuns(page); // your own state logic
                }}
                onItemsPerPageChange={(value) => {
                  setCurrentPage(value);
                  setCurrentPageHarvestRuns(value);
                }}
              // showPageSizeSelector={false} // hide dropdown if you don’t need it
              // showItemCount={true}
              /></div>)}
            </CardContent>

          </Card>
          <AlertDialog open={showRunDialog} onOpenChange={setShowRunDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  {/* <Play className="w-5 h-5 text-blue-600" /> */}
                  Re Run Chart
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Select an prompt to re run this chart. The chart will re-run using the selected AI prompt configuration.

                </AlertDialogDescription>
              </AlertDialogHeader>
              <div>
                <div className='flex gap-2 items-start bg-gray-100 p-2 rounded'>
                  <div>
                    <CircleUser className="w-6 h-6" />
                  </div>
                  <div className=''>
                    <p className='font-inter font-semibold text-base'>{selectedRerunCharts?.client_name} <span className="font-bold">{selectedRerunCharts?.client_id}</span></p>
                    <p className='font-inter text-gray-600 text-sm'><span>Clinician: </span>{selectedRerunCharts?.clinician_name}</p>
                    <p className='font-inter text-gray-500 text-xs'><span>Visit Date: </span>{selectedRerunCharts?.visit_date}</p>
                  </div>
                </div>
              </div>
              {/* <div className='flex justify-between items-center'> */}
              <div className='flex flex-col gap-y-2'>
                {/* <Checkbox
                              // checked={typeOfRun === 1}
                              onCheckedChange={(checked) => {
                                setTypeOfRun(checked === true ? 1 : 2)
                              }} /> */}
                <div className="font-semibold text-sm text-gray-800 ml-1">Select Prompt</div>
                <Select value={selectedPrompt} onValueChange={(value) => {
                  setSelectedPrompt(value);
                  const selectedValue = dropdownVal?.find((item: any) => item.version_id === value);
                  if (selectedValue) {
                    setRunFlag(selectedValue.status === "ACTIVE" ? 2 : 1);
                  }
                }}>
                  <SelectTrigger className="w-full truncate">
                    <SelectValue className="capitalize" placeholder="Select Batch" />
                  </SelectTrigger>
                  <SelectContent className='h-auto overflow-y-auto'>
                    {dropdownVal &&
                      dropdownVal?.map((value: any) => (
                        <SelectItem key={value.version_id} value={value.version_id} className="capitalize overflow-hidden">
                          {value.name}  <span className={`${value.status === "ACTIVE" ? "bg-green-600" : "bg-gray-600"} text-white text-xs px-2 py-1 rounded ml-2`}>{value.status}</span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>


              </div>
              <AlertDialogFooter>
                {/* <AlertDialogCancel onClick={()=>setSelectedBatchFile(null)}>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={confirmRunHarvest}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Start Chart Review Run
                            </AlertDialogAction> */}

                {/* <div className='flex gap-2'>
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowRunDialog(false);
                  // handleReTrychart(runData?.id, runData?.chart_id);
                }}
                className=" text-Black"
              >
                <Play className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
            <div> */}
                {isRerunPolling && (
                  <div className="mr-auto flex items-center gap-2 text-sm text-blue-600">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Re-run in progress...
                  </div>
                )}
                <AlertDialogCancel onClick={() => setSelectedPrompt(null)}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  className={`bg-blue-600 hover:bg-blue-700 ${(!selectedPrompt || isRerunPolling) ? 'pointer-events-none opacity-50' : ''}`}
                  onClick={ReRunChart}
                >
                  Start Re-Run
                </AlertDialogAction>
                {/* </div>
          </div> */}
              </AlertDialogFooter>
              {/* </div> */}
            </AlertDialogContent>
          </AlertDialog>
          <Dialog open={selectedUndoCharts} onOpenChange={() => setSelectedUndoCharts(null)}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Client ID: {selectedUndoCharts?.client_id}</DialogTitle>
                <DialogDescription>
                  Are you sure you want to undo the decision of this chart?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedUndoCharts(null)}>
                  Close
                </Button>
                <Button variant="approve" onClick={UndoChartsDecision}>
                  {/* <Undo className="w-4 h-4" /> */}
                  Undo
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </Layout>
  );
}